<!--
  Trang tổng quan dành cho role 'agency' (đại lý/đối tác).
  Hiển thị: (1) vòng đời 1 khách hàng từ lúc gắn với đại lý → tạo & quản lý người dùng;
            (2) tài liệu API đơn giản để quản trị Client / Client User.
  Nhúng trong Dashboard.vue với v-if role === 'agency'.
-->
<template>
  <div class="agency-overview">
    <!-- ░░ Vòng đời khách hàng ░░ -->
    <div class="tile">
      <h4 class="tile-title">
        <span class="ao-head-icon"><font-awesome-icon icon="route" /></span>
        {{ $t('lifecycleTitle') }}
      </h4>
      <p class="ao-sub">{{ $t('lifecycleSub') }}</p>

      <div class="ao-steps">
        <div class="ao-step" v-for="(s, i) in steps" :key="i">
          <div class="ao-step-no">{{ i + 1 }}</div>
          <div class="ao-step-body">
            <div class="ao-step-name"><font-awesome-icon :icon="s.icon" class="ao-step-icon" /> {{ s.name }}</div>
            <ul class="ao-point-list">
              <li v-for="(p, k) in s.points" :key="k"><span v-html="p"></span></li>
            </ul>
            <!-- Hộp link giới thiệu + nút sao chép (chỉ ở bước Gắn khách) -->
            <div v-if="s.referral" class="ao-reflink">
              <span class="ao-reflink-label">{{ $t('refLinkLabel') }}</span>
              <code class="ao-reflink-url">{{ referralLink }}</code>
              <button type="button" class="ao-reflink-copy"
                      v-clipboard:copy="referralLink" v-clipboard:success="onCopy" v-clipboard:error="onError">
                <font-awesome-icon icon="copy" /> {{ copied ? $t('copied') : $t('copy') }}
              </button>
            </div>
            <div v-if="s.note" class="ao-step-note"><font-awesome-icon icon="info-circle" /> {{ s.note }}</div>
            <router-link v-if="s.link" :to="s.link" class="ao-step-link">
              {{ s.linkText }} <i class="fa fa-angle-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ░░ Tài liệu API ░░ -->
    <div class="tile" :class="{ 'ao-tile-collapsed': !apiOpen }">
      <h4 class="tile-title ao-collapse-head" :class="{ 'ao-open': apiOpen }" @click="apiOpen = !apiOpen">
        <span class="ao-head-icon"><font-awesome-icon icon="code" /></span>
        {{ $t('apiTitle') }}
        <i class="fa" :class="apiOpen ? 'fa-angle-up' : 'fa-angle-down'" style="margin-left:auto"></i>
      </h4>

      <div v-show="apiOpen">
        <!-- Base URL + xác thực -->
        <div class="ao-api-block">
          <div class="ao-api-label">{{ $t('baseUrl') }}</div>
          <code class="ao-code-inline">{{ baseUrl }}</code>
        </div>
        <div class="ao-api-block">
          <div class="ao-api-label">{{ $t('authLabel') }}</div>
          <p class="ao-api-note" v-html="$t('authNote')"></p>
          <pre class="ao-code">token: &lt;API_TOKEN&gt;
{{ '#' }} hoặc
Authorization: Bearer &lt;API_TOKEN&gt;</pre>
        </div>

        <!-- Bảng endpoint -->
        <div class="ao-api-block" v-for="grp in apiGroups" :key="grp.name">
          <div class="ao-api-group">{{ grp.name }}</div>
          <table class="table table-sm ao-api-table">
            <thead>
              <tr>
                <th style="width:80px">{{ $t('method') }}</th>
                <th>Endpoint</th>
                <th>{{ $t('desc') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(ep, idx) in grp.endpoints" :key="idx">
                <td><span class="ao-method" :class="'ao-m-' + ep.m.toLowerCase()">{{ ep.m }}</span></td>
                <td><code>{{ ep.path }}</code></td>
                <td>{{ ep.desc }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ví dụ curl -->
        <div class="ao-api-block">
          <div class="ao-api-label">{{ $t('exampleLabel') }}</div>
          <pre class="ao-code">{{ curlExample }}</pre>
        </div>

        <p class="ao-api-foot">{{ $t('apiFoot') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgencyOverview',
  data() {
    return {
      apiOpen: false,
      copied: false,
      baseUrl: process.env.VUE_APP_SERVICE_URL || 'https://adminapi.phonenet.io',
      clientUrl: process.env.VUE_APP_CLIENT_URL || 'https://client.callio.vn',
    };
  },
  computed: {
    // Link giới thiệu của đại lý: khách đăng ký qua link này sẽ tự gắn vào đại lý (client.agency = bạn).
    referralLink() {
      return `${this.clientUrl}/register/${this.$store.state.user._id}`;
    },
    steps() {
      return [
        {
          icon: 'link', name: this.$t('s1Name'),
          points: [this.$t('s1p1'), this.$t('s1p2'), this.$t('s1p3'), this.$t('s1p4')],
          referral: true,
          note: this.$t('s1Note'), link: '/client', linkText: this.$t('goClient'),
        },
        {
          icon: 'box', name: this.$t('s2Name'),
          points: [this.$t('s2p1'), this.$t('s2p2'), this.$t('s2p3')],
        },
        {
          icon: 'users', name: this.$t('s3Name'),
          points: [this.$t('s3p1'), this.$t('s3p2')],
          link: '/client', linkText: this.$t('goClient'),
        },
        {
          icon: 'sign-in-alt', name: this.$t('s4Name'),
          points: [this.$t('s4p1'), this.$t('s4p2')],
        },
        {
          icon: 'chart-line', name: this.$t('s5Name'),
          points: [this.$t('s5p1'), this.$t('s5p2')],
        },
      ];
    },
    apiGroups() {
      return [
        {
          name: this.$t('grpClient'),
          endpoints: [
            { m: 'GET', path: '/client', desc: this.$t('epClientList') },
            { m: 'GET', path: '/client/:id', desc: this.$t('epClientGet') },
            { m: 'POST', path: '/client', desc: this.$t('epClientCreate') },
            { m: 'PUT', path: '/client/:id', desc: this.$t('epClientUpdate') },
          ],
        },
        {
          name: this.$t('grpUser'),
          endpoints: [
            { m: 'GET', path: '/client-user?client=:clientId', desc: this.$t('epUserList') },
            { m: 'GET', path: '/client-user/:id', desc: this.$t('epUserGet') },
            { m: 'POST', path: '/client-user', desc: this.$t('epUserCreate') },
            { m: 'PUT', path: '/client-user/:id', desc: this.$t('epUserUpdate') },
            { m: 'POST', path: '/client-user/activate/:id', desc: this.$t('epUserActivate') },
            { m: 'POST', path: '/client-user/deactivate/:id', desc: this.$t('epUserDeactivate') },
            { m: 'PUT', path: '/client-user/:id/password', desc: this.$t('epUserPassword') },
            { m: 'GET', path: '/client-user/token/:id', desc: this.$t('epUserToken') },
          ],
        },
        {
          name: this.$t('grpRef'),
          endpoints: [
            { m: 'GET', path: '/plan', desc: this.$t('epPlan') },
            { m: 'GET', path: '/country', desc: this.$t('epCountry') },
            { m: 'GET', path: '/host', desc: this.$t('epHost') },
            { m: 'GET', path: '/category', desc: this.$t('epCategory') },
          ],
        },
      ];
    },
    curlExample() {
      return `curl -X POST '${this.baseUrl}/client-user' \\
  -H 'token: <API_TOKEN>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client": "<CLIENT_ID>",
    "name": "Nguyen Van A",
    "email": "a@khachhang.com",
    "ext": "1001",
    "extPassword": "matkhauNhanh",
    "password": "matkhauDangNhap",
    "role": "agent"
  }'`;
    },
  },
  methods: {
    onCopy() {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    },
    onError() {
      this.$swal && this.$swal({ text: this.$t('copyFailed'), icon: 'error', toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });
    },
  },
  i18n: {
    messages: {
      vi: {
        lifecycleTitle: 'Vòng đời khách hàng',
        lifecycleSub: 'Các bước từ lúc khách được gắn với đại lý của bạn đến khi vận hành & quản lý người dùng.',
        s1Name: 'Gắn khách với đại lý',
        s1p1: '<b>Link giới thiệu (khuyên dùng):</b> chia sẻ link bên dưới cho khách. Khách đăng ký qua link này sẽ <b>tự động gắn vào đại lý của bạn</b> — bạn thấy ngay trong danh sách khách.',
        s1p2: '<b>Bạn tự tạo khách mới:</b> vào menu Khách hàng → Thêm. Hệ thống tự gán đại lý = bạn.',
        s1p3: '<b>Khách đăng ký qua domain đối tác:</b> nếu bạn có domain riêng (white-label) do Callio cấp, khách đăng ký trên domain đó cũng tự gắn vào đại lý của bạn.',
        s1p4: '<b>Callio gán khách cho bạn:</b> quản trị Callio gán một khách có sẵn cho đại lý của bạn.',
        s1Note: 'Bạn chỉ thấy & quản lý các khách thuộc đại lý của mình.',
        refLinkLabel: 'Link đăng ký của bạn:',
        copy: 'Sao chép',
        copied: 'Đã sao chép!',
        copyFailed: 'Sao chép thất bại',
        s2Name: 'Cấp gói & tổng đài',
        s2p1: '<b>Gói dịch vụ (Plan):</b> chọn gói khi tạo/sửa khách.',
        s2p2: '<b>Tổng đài & domain:</b> khách được cấp tổng đài (IP) và domain riêng để sử dụng.',
        s2p3: '<b>Trạng thái:</b> Dùng thử → Đang hoạt động.',
        s3Name: 'Tạo & quản lý người dùng',
        s3p1: '<b>Thêm người dùng:</b> nhập số nhánh (ext), email, mật khẩu và quyền (Nhân viên / Trưởng nhóm / Chủ tài khoản).',
        s3p2: '<b>Quản lý:</b> sửa thông tin, khoá / mở khoá, đổi mật khẩu — ngay trong màn hình chi tiết khách.',
        s4Name: 'Đăng nhập hộ để hỗ trợ',
        s4p1: 'Lấy token đăng nhập vào tài khoản người dùng của khách để cấu hình / hỗ trợ thay khách.',
        s4p2: 'Dùng khi khách cần bạn thao tác giúp mà không phải chia sẻ mật khẩu.',
        s5Name: 'Vận hành & theo dõi',
        s5p1: 'Khách bắt đầu sử dụng dịch vụ (gọi, chat, CRM…).',
        s5p2: 'Bạn theo dõi danh sách khách, người dùng và trạng thái hoạt động của từng khách.',
        goClient: 'Tới Khách hàng',
        apiTitle: 'Tài liệu API — quản trị Khách hàng / Người dùng',
        baseUrl: 'Base URL',
        authLabel: 'Xác thực',
        authNote: 'Mọi request gắn API token vào header. Tạo token tại menu <b>Cài đặt → Token</b>.',
        method: 'Method',
        desc: 'Mô tả',
        grpClient: 'Khách hàng (Client)',
        grpUser: 'Người dùng của khách (Client User)',
        grpRef: 'Dữ liệu tham chiếu (chỉ đọc)',
        epClientList: 'Danh sách khách của đại lý (tự lọc theo bạn). Hỗ trợ page, pageSize.',
        epClientGet: 'Chi tiết 1 khách (phải thuộc đại lý của bạn).',
        epClientCreate: 'Tạo khách mới — hệ thống tự gán đại lý = bạn.',
        epClientUpdate: 'Cập nhật thông tin khách của bạn.',
        epUserList: 'Danh sách người dùng của 1 khách.',
        epUserGet: 'Chi tiết 1 người dùng.',
        epUserCreate: 'Tạo người dùng cho khách (body có client, name, email, ext, extPassword, password, role).',
        epUserUpdate: 'Cập nhật người dùng.',
        epUserActivate: 'Mở khoá (kích hoạt) người dùng.',
        epUserDeactivate: 'Khoá người dùng.',
        epUserPassword: 'Đổi mật khẩu đăng nhập của người dùng.',
        epUserToken: 'Lấy token đăng nhập hộ vào tài khoản người dùng.',
        epPlan: 'Danh sách gói dịch vụ.',
        epCountry: 'Danh sách quốc gia / múi giờ.',
        epHost: 'Danh sách tổng đài (host).',
        epCategory: 'Danh sách danh mục.',
        exampleLabel: 'Ví dụ — tạo người dùng',
        apiFoot: 'Lưu ý: đại lý chỉ thao tác được trên khách & người dùng thuộc đại lý của mình; gọi sang khách khác sẽ bị từ chối (403/404).',
      },
      en: {
        lifecycleTitle: 'Customer lifecycle',
        lifecycleSub: 'Steps from when a customer is linked to your agency until you operate & manage its users.',
        s1Name: 'Link customer to agency',
        s1p1: '<b>Referral link (recommended):</b> share the link below with your customer. Anyone registering through it is <b>auto-linked to your agency</b> — you see them immediately in your customer list.',
        s1p2: '<b>You create the customer:</b> go to Customers → Add. The system auto-assigns agency = you.',
        s1p3: '<b>Customer registers via partner domain:</b> if you have a dedicated (white-label) domain from Callio, customers registering on it are also auto-linked to your agency.',
        s1p4: '<b>Callio assigns a customer:</b> a Callio admin assigns an existing customer to your agency.',
        s1Note: 'You only see & manage customers under your own agency.',
        refLinkLabel: 'Your registration link:',
        copy: 'Copy',
        copied: 'Copied!',
        copyFailed: 'Copy failed',
        s2Name: 'Provision plan & PBX',
        s2p1: '<b>Service plan:</b> choose a plan when creating/editing the customer.',
        s2p2: '<b>PBX & domain:</b> the customer gets a PBX (IP) and its own domain.',
        s2p3: '<b>Status:</b> Trial → Active.',
        s3Name: 'Create & manage users',
        s3p1: '<b>Add a user:</b> ext number, email, password and role (Agent / Supervisor / Owner).',
        s3p2: '<b>Manage:</b> edit info, lock / unlock, reset password — right in the customer detail screen.',
        s4Name: 'Impersonate for support',
        s4p1: 'Get a login token to enter the customer user account to configure / support on their behalf.',
        s4p2: 'Use it when the customer needs your help without sharing their password.',
        s5Name: 'Operate & monitor',
        s5p1: 'The customer starts using the service (calls, chat, CRM…).',
        s5p2: 'You monitor customers, users and each customer’s activity status.',
        goClient: 'Go to Customers',
        apiTitle: 'API docs — manage Customers / Users',
        baseUrl: 'Base URL',
        authLabel: 'Authentication',
        authNote: 'Attach the API token to the header of every request. Create a token at <b>Settings → Token</b>.',
        method: 'Method',
        desc: 'Description',
        grpClient: 'Customer (Client)',
        grpUser: 'Customer user (Client User)',
        grpRef: 'Reference data (read-only)',
        epClientList: 'List your agency customers (auto-filtered). Supports page, pageSize.',
        epClientGet: 'Get one customer (must belong to your agency).',
        epClientCreate: 'Create a customer — auto-assigned agency = you.',
        epClientUpdate: 'Update your customer.',
        epUserList: 'List users of a customer.',
        epUserGet: 'Get one user.',
        epUserCreate: 'Create a user (body: client, name, email, ext, extPassword, password, role).',
        epUserUpdate: 'Update a user.',
        epUserActivate: 'Activate (unlock) a user.',
        epUserDeactivate: 'Lock a user.',
        epUserPassword: 'Reset a user login password.',
        epUserToken: 'Get a token to log in as the user.',
        epPlan: 'List service plans.',
        epCountry: 'List countries / timezones.',
        epHost: 'List PBX hosts.',
        epCategory: 'List categories.',
        exampleLabel: 'Example — create a user',
        apiFoot: 'Note: an agency can only operate on customers & users under its own agency; calls to other customers are rejected (403/404).',
      },
    },
  },
};
</script>

<style scoped>
.agency-overview .tile-title {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  font-size: 15px;
}
.ao-head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 8px;
  border-radius: 6px;
  background: #f0f3f7;
  color: #4a90d9;
  font-size: 14px;
}
.ao-sub { color: #8a929c; font-size: 13px; margin: 0 0 16px; }

/* Khối API thu gọn: bỏ gạch dưới tiêu đề + thu nhỏ khoảng trắng */
.ao-collapse-head { cursor: pointer; }
.ao-collapse-head:not(.ao-open) {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.tile.ao-tile-collapsed { padding-bottom: 16px; }

/* Steps */
.ao-steps { display: flex; flex-direction: column; gap: 2px; }
.ao-step { display: flex; gap: 14px; padding: 14px 0; border-bottom: 1px dashed #eef1f5; }
.ao-step:last-child { border-bottom: none; }
.ao-step-no {
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%;
  background: #4a90d9; color: #fff; font-weight: 600;
  display: flex; align-items: center; justify-content: center; font-size: 14px;
}
.ao-step-body { min-width: 0; }
.ao-step-name { font-weight: 600; font-size: 14px; color: #33404f; margin-bottom: 6px; }
.ao-step-icon { color: #4a90d9; margin-right: 4px; }
.ao-point-list {
  list-style: none; margin: 0; padding: 0;
}
.ao-point-list li {
  position: relative;
  padding-left: 16px;
  margin-bottom: 5px;
  font-size: 13px;
  color: #5a6675;
  line-height: 1.55;
}
.ao-point-list li:before {
  content: '';
  position: absolute; left: 2px; top: 8px;
  width: 5px; height: 5px; border-radius: 50%;
  background: #b7c2cf;
}
.ao-point-list li >>> b { color: #33404f; }
.ao-point-list li >>> code {
  background: #f5f7fa; border: 1px solid #e3e8ee; border-radius: 3px;
  padding: 1px 5px; color: #2f6f9f; font-size: 12px;
}
.ao-step-note {
  font-size: 12.5px; color: #8a929c; margin: 6px 0 2px; font-style: italic;
}
.ao-step-note svg { color: #b0b8c4; margin-right: 3px; }
.ao-step-link { display: inline-block; margin-top: 6px; font-size: 12.5px; color: #4a90d9; text-decoration: none; }
.ao-step-link:hover { text-decoration: underline; }

/* Hộp link giới thiệu + copy */
.ao-reflink {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  margin: 8px 0 2px; padding: 8px 10px;
  background: #eef5ff; border: 1px solid #d4e4fb; border-radius: 6px;
}
.ao-reflink-label { font-size: 12.5px; font-weight: 600; color: #33404f; }
.ao-reflink-url {
  flex: 1 1 auto; min-width: 180px;
  background: #fff; border: 1px solid #d4e4fb; border-radius: 4px;
  padding: 4px 8px; color: #1c5fa8; font-size: 12.5px;
  overflow-x: auto; white-space: nowrap;
}
.ao-reflink-copy {
  flex: 0 0 auto; border: none; border-radius: 4px;
  background: #1c7ed6; color: #fff; font-size: 12px; font-weight: 600;
  padding: 5px 12px; cursor: pointer;
}
.ao-reflink-copy:hover { background: #1971c2; }

/* API docs */
.ao-api-block { margin-bottom: 16px; }
.ao-api-label { font-weight: 600; font-size: 13px; color: #33404f; margin-bottom: 6px; }
.ao-api-note { font-size: 13px; color: #6b7480; margin: 0 0 6px; }
.ao-api-group { font-weight: 600; font-size: 13px; color: #4a90d9; margin: 4px 0 6px; }
.ao-code-inline {
  display: inline-block; padding: 4px 10px; background: #f5f7fa;
  border: 1px solid #e3e8ee; border-radius: 4px; color: #d6336c; font-size: 13px;
}
.ao-code {
  background: #2d333b; color: #e6edf3; padding: 12px 14px; border-radius: 6px;
  font-size: 12.5px; line-height: 1.55; overflow-x: auto; margin: 0;
  white-space: pre;
}
.ao-api-table { font-size: 13px; }
.ao-api-table th { background: #f1f5fb; font-weight: 600; color: #5a6675; }
.ao-api-table code { color: #2f6f9f; font-size: 12.5px; }
.ao-method {
  display: inline-block; min-width: 48px; text-align: center; padding: 2px 6px;
  border-radius: 4px; font-size: 11px; font-weight: 700; color: #fff;
}
.ao-m-get { background: #2f9e44; }
.ao-m-post { background: #1c7ed6; }
.ao-m-put { background: #f08c00; }
.ao-api-foot { font-size: 12px; color: #8a929c; margin: 4px 0 0; font-style: italic; }
</style>
